/**
 * Reconcile decision logic for a dashboard's movable count panels (#33552; LC Dashboards
 * and Accounting Dashboards).
 *
 * GridStack reads a tile's gs-* attributes only when it adopts the tile, so a saved
 * layout that lands after the grid is built has to be pushed in explicitly. These
 * helpers decide what to push and whether a push is needed at all — extracted from
 * LCDashboards, now shared, so the logic that guards against overwriting a user's
 * arrangement is unit-testable without mounting the page.
 */

/**
 * The saved shape (structurally satisfied by panelGridTypes' PanelLayoutItem and
 * LCDashboards' private copy).
 */
export interface PanelGeometrySource {
  x: number;
  y: number;
  w: number;
  h: number;
  collapsed?: boolean;
  locked?: boolean;
}

/** The live GridStack node (all fields optional — GridStack populates them lazily). */
export interface PanelGeometryNode {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  locked?: boolean;
  noMove?: boolean;
  noResize?: boolean;
}

export interface PanelGeometryTarget {
  x: number;
  y: number;
  w: number;
  h: number;
  /** GridStack's own pin — stops the packing algorithm relocating the tile. */
  locked: boolean;
  /** Takes the user's drag handle away. */
  noMove: boolean;
  /** Takes the user's resize handle away. */
  noResize: boolean;
}

/**
 * The GridStack options a saved item should be applied as.
 *
 * A collapsed tile lives at h=1 on the grid regardless of the height it restores to,
 * and all three lock flags are set together: `locked` is what actually pins a tile
 * against compaction, while noMove/noResize only remove the user's handles. Setting
 * only the latter two is why "locked" LC panels still drifted under float:false.
 */
export const panelGeometryTarget = (item: PanelGeometrySource): PanelGeometryTarget => {
  const locked = !!item.locked;
  return {
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.collapsed ? 1 : item.h,
    locked,
    noMove: locked,
    noResize: locked,
  };
};

/**
 * Whether the live node already matches the target. Keeping this exact makes the
 * reconcile pass idempotent, so it can run on every layout change — including the ones
 * the grid's own 'change' handler produces — without fighting the user mid-drag.
 *
 * A node with no geometry yet (fresh, un-laid-out) always needs an update.
 */
export const panelGeometryNeedsUpdate = (
  node: PanelGeometryNode | undefined | null,
  target: PanelGeometryTarget,
): boolean => {
  if (!node) return true;
  const geometryDiffers = node.x !== target.x
    || node.y !== target.y
    || node.w !== target.w
    || node.h !== target.h;
  const lockDiffers = !!node.locked !== target.locked
    || !!node.noMove !== target.noMove
    || !!node.noResize !== target.noResize;
  return geometryDiffers || lockDiffers;
};

/** A saved layout, keyed by workspace. */
export type PanelArrangement = { [workspaceId: string]: { key: string; x: number; w: number }[] };

/**
 * Whether a saved layout still looks like the code default — used to keep colleagues
 * who never arranged their board out of the "copy a layout" picker.
 *
 * Compares **x and w only**, deliberately. Heights and y positions drift on their own:
 * growPanelToFitContent adds rows as a panel's content grows and float:false compaction
 * re-packs everything below it, so almost nobody's h/y matches the default even when
 * they have never touched a tile. Column position and width only change when someone
 * drags or resizes, which makes them the signal for a deliberate arrangement. (Verified
 * against prod: 35 of 71 saved layouts sit at default x/w with drifted y/h.)
 *
 * Panels absent from the default are ignored — a tile added after the default was
 * authored (e.g. pending-edi-orders) auto-flows into everyone's layout and would
 * otherwise make every user look customised.
 */
export const isDefaultPanelArrangement = (
  layouts: PanelArrangement | undefined | null,
  defaults: PanelArrangement,
): boolean => {
  if (!layouts) return true;
  return Object.entries(defaults).every(([workspaceId, defaultItems]) => {
    const items = layouts[workspaceId];
    if (!items || items.length === 0) return true; // never saved — still the default
    const byKey = new Map(items.map((i) => [i.key, i]));
    return defaultItems.every((def) => {
      const mine = byKey.get(def.key);
      if (!mine) return true; // panel not present (permission-gated, or removed)
      return mine.x === def.x && mine.w === def.w;
    });
  });
};
